import dayjs from "dayjs"
import IconCellRenderer from "components/ag-grid-cell-renderers/IconCellRenderer/IconCellRenderer"
import ActionItemPriority from "components/ag-grid-cell-renderers/ActionItemPriority/ActionItemPriority"
import PrsUploadStatusCell from "components/ag-grid-cell-renderers/PrsUploadStatusCell/PrsUploadStatusCell"

// constants
import { phaseShortcutMap } from "containers/studies/Study/subcomponents/Authoring/RJS/schemas/dataSchemas/enum/enum"

// cell renderers
import ActionItemFormsCSRM from "components/ag-grid-cell-renderers/action-items/ActionItemFormsCSRM/ActionItemFormsCSRM"
import ActionItemForms from "components/ag-grid-cell-renderers/action-items/ActionItemForms/ActionItemForms"
import ActionItemDocs from "components/ag-grid-cell-renderers/action-items/ActionItemDocs/ActionItemDocs"
import CountryFlags from "components/ag-grid-cell-renderers/CountryFlags/CountryFlags"

const controlledGridProps = {
  buttons: ["clear", "apply"],
  closeOnApply: true,
}

const dateColDef = {
  cellRenderer: "Date",
  filter: "agDateColumnFilter",
  minWidth: 120,
  // filterParams only called for CSRM grids
  filterParams: {
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      const dateAsString = cellValue
      if (!cellValue) return null // do not include blank (including empty string) values in results
      const cellDate = dayjs(dateAsString).toDate().toString()
      if (dayjs(cellDate).isBefore(filterLocalDateAtMidnight, "day")) {
        return -1
      } else if (dayjs(cellDate).isAfter(filterLocalDateAtMidnight, "day")) {
        return 1
      } else {
        return 0
      }
    },
  },
}

const getDateColDef = ({ gridIsControlled } = {}) => {
  const defs = { ...dateColDef }
  if (gridIsControlled) {
    defs.filterParams = {
      ...dateColDef.filterParams,
      ...controlledGridProps,
    }
  }
  return defs
}

/**
 * if "gridIsControlled" true, will add "Apply" button to the filter window. This is to fix an issue where the date picker would close when navigating between months and years and not allowing the user to choose the date properly.
 */
const getDateColDefMonthYear = ({ gridIsControlled } = {}) => {
  const filterParams = {
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      if (!cellValue) return 0

      // Parse MM/YY into a valid date, setting day to 1
      const [month, year] = cellValue.split("/").map(Number)
      if (!month || !year) return 0

      // Convert 2-digit year to 4-digit year (adjust logic as needed)
      const fullYear = year < 100 ? 2000 + year : year

      // Construct a date at the start of the month
      const cellDate = dayjs(`${fullYear}-${month}-01`).toDate()

      // Compare only month and year
      if (dayjs(cellDate).isBefore(filterLocalDateAtMidnight, "month")) {
        return -1
      } else if (dayjs(cellDate).isAfter(filterLocalDateAtMidnight, "month")) {
        return 1
      } else {
        return 0
      }
    },
  }
  const defs = {
    filter: "agDateColumnFilter",
    filterParams,
  }
  if (gridIsControlled) {
    defs.filterParams = {
      ...filterParams,
      ...controlledGridProps,
    }
  }
  return defs
}

/**
 * if "gridIsControlled" true, will add "Apply" button to the filter window. This is to fix an issue where the date picker would close when navigating between months and years and not allowing the user to choose the date properly.
 */
const getDateColDefSeconds = ({ gridIsControlled } = {}) => {
  const filterParams = {
    // NOTE: comparing as date without seconds
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      // UTC date converted to local time zone on DateSecond component
      const dateAsString = dayjs.utc(cellValue).local()
      if (!cellValue) return null // do not include blank (including empty string) values in results
      const cellDate = dayjs(dateAsString).toDate()
      if (dayjs(cellDate).isBefore(filterLocalDateAtMidnight, "day")) {
        return -1
      } else if (dayjs(cellDate).isAfter(filterLocalDateAtMidnight, "day")) {
        return 1
      } else {
        return 0
      }
    },
  }
  const defs = {
    cellRenderer: "DateSecond",
    filter: "agDateColumnFilter",
    // filterParams only called for CSRM grids
    filterParams,
  }
  if (gridIsControlled) {
    defs.filterParams = {
      ...filterParams,
      ...controlledGridProps,
    }
  }
  return defs
}

/**
 * Return function to convert date to "YYYY-MM-DD" format needed for CSRM AG Grid filtering and sorting.
 *
 * Use with following columnDefs properties, which will not be called on SSRM grids), eg:
 *   getValue: dateFormatter('curr_load_date')
 *      -- or --
 *   filtergetValue: dateFormatter('curr_load_date')
 *
 * need to send field name because value not provided in getValue or filtergetValue params.
 */
const dateFormatter = (fieldName) => {
  return ({ data }) => {
    let value = data[fieldName]
    if (!value) return null
    return dayjs(value).format("YYYY-MM-DD")
  }
}

const getRolesColDef = (roles, options = {}) => {
  if (roles) {
    return roles.map((role) => ({
      headerName: role?.name?.replace("_", " ").replace("PAREN_LEFT", "(").replace("PAREN_RIGHT", ")"),
      field: role?.name?.replace(/ /g, "_").replace("(", "PAREN_LEFT").replace(")", "PAREN_RIGHT"),
      initialHide: true,
      cellRenderer: "UserFullnameCellRenderer",
      minWidth: 180,
      autofilter: {
        app: "authentication",
        model: "PactUser",
        sub_field: "fullname",
      },
      ...options,
    }))
  }

  return []
}

const largeColDef = {
  filter: "agTextColumnFilter",
  minWidth: 150,
  width: 400,
}

const favoritesColDef = ({ type, studyIdKey } = {}) => {
  return {
    headerValueGetter: ({ location }) => {
      switch (location) {
        case "columnToolPanel":
          return "Favorite"
        default: // case "header"
          return ""
      }
    },
    field: "is_favorite",
    cellRenderer: type === "request" ? "FavoriteRequestCellRenderer" : "FavoriteStudyCellRenderer",
    cellRendererParams: type === "study" ? { studyIdKey } : {},
    width: 45,
    menuTabs: [],
    filter: false,
    sortable: false,
    resizable: false,
    suppressSizeToFit: true,
    pinned: "left",
    cellClass: "favorite-cell",
  }
}

// NOTE: other field option besides "record_status": "study__data__record_status"
const getRecordStatusColDef = (field = "record_status") => {
  let key = field
  if (field) key = field
  return {
    headerName: "Record Status",
    field: key,
    filterParams: {
      values: ["(Blanks)", "Verified", "Unverified", "Archived"],
    },
    cellRenderer: "RecordStatusBadge",
    cellClass: ["col-badge"],
    width: 110,
  }
}

const phaseColDef = {
  headerName: "Phase",
  field: "phase",
  filterParams: {
    values: ["(Blanks)", ...Object.keys(phaseShortcutMap)],
  },
  cellRenderer: "PhaseBadge",
  cellClass: ["col-badge"],
}

const getEditColDef = ({
  colId,
  onClick,
  isAllowed, // TODO: rename to disabled
  tooltip,
} = {}) => {
  let colDefs = {
    colId: "edit",
    headerValueGetter: ({ location }) => {
      switch (location) {
        case "columnToolPanel":
          return "Edit"
        default: // case "header"
          return ""
      }
    },
    headerTooltip: "Edit",
    cellRenderer: IconCellRenderer,
    cellRendererParams: {
      iconClassName: "fal fa-edit",
      onClick: onClick ? onClick : null,
      isAllowed,
      tooltip,
    },
    width: 43,
    cellStyle: { textAlign: "center" },
    menuTabs: [],
    sortable: false,
  }
  colDefs.colId = colId || "edit"
  return colDefs
}

const getDeleteColDef = ({ isAllowed = true, onClick }) => {
  return {
    colId: "delete",
    headerValueGetter: ({ location }) => {
      switch (location) {
        case "columnToolPanel":
          return "Delete"
        default: // case "header"
          return ""
      }
    },
    headerTooltip: "Delete",
    cellRenderer: IconCellRenderer,
    cellRendererParams: {
      iconClassName: "delete-icon-renderer fal fa-trash",
      onClick: onClick ? onClick : null,
      isAllowed,
    },
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
    width: 40,
  }
}

// priority dot
const actionPriorityColDef = {
  headerName: "",
  field: "priority",
  cellRenderer: ActionItemPriority,
  width: 32,
  menuTabs: [],
  resizable: false,
  pinned: "left",
}

// cellRenderer defined by grid
const openFileColDef = {
  headerName: "",
  headerTooltip: "Open file",
  colId: "openFile",
  width: 37,
  menuTabs: [],
  sortable: false,
  resizable: false,
  suppressSizeToFit: true,
  suppressColumnsToolPanel: true,
  pinned: "left",
}

const uploadStatusColDef = {
  valueGetter: ({ data }) => {
    const value = data?.upload_status
    if (value === "UNCHANGED_STUDY") {
      return "Unchanged Study"
    } else if (value === "CHANGED_STUDY") {
      return "Changed Study"
    } else if (value === "NEW_STUDY") {
      return "New Study"
    } else if (
      (!value || (value && Array.isArray(value)) || (value && typeof value === "string" && value.includes("ERROR"))) &&
      (data?.upload_type || data?.last_upload_prs)
    ) {
      return "Failed"
    }
    return null
  },
  cellRenderer: PrsUploadStatusCell,
}

// Given any array, sort by the number of items in the array. Used by the form(s) and file(s) columns
const itemCountComparator = (valueA, valueB) => {
  // if both zero or empty values
  if ((!valueA || valueA.length === 0) && (!valueB || valueB.length === 0)) return 0
  // if either value is empty
  if (!valueA) return -1
  if (!valueB) return 1
  return valueA.length > valueB.length ? 1 : -1
}

// forms column on client-side row model (CSRM) action items grids
const formsColDefCsrm = () => {
  return {
    headerName: "Form(s)",
    field: "forms",
    cellRenderer: ActionItemFormsCSRM,
    valueGetter: ({ data }) => {
      if (!data?.forms) return null
      return data.forms.map((item) => {
        if (typeof item === "object") {
          return `${item.form_view} ${item.form_section.replace(/_/g, " ")}`
        } else if (typeof item === "string") {
          return item.replace(/_/g, " ")
        }
      })
    },
    comparator: itemCountComparator,
  }
}

export const abbrevationMap = {
  PRA: "Protocol Approval Date",
  FSA: "First Site Activation Date",
  FPI: "Study Start Date",
  PCD: "Primary Completion Date",
  LPO: "Study Completion Date",
  FCSR: "Clinical Study Report Completed",
}

const isValidDate = (value) => {
  const date = new Date(value)
  return date instanceof Date && !isNaN(date.getTime())
}

const getMilestoneDateFieldCols = ({ dateFields, initialHide }) => {
  return dateFields.map((fieldName) => {
    const dateKey = fieldName.toLowerCase()

    const commonDateSubColDef = {
      columnGroupShow: "open",
      ...dateColDef,
      initialHide,
    }

    const safeValueGetter =
      (type) =>
      ({ data }) => {
        const val = data?.[dateKey]?.[type]
        return isValidDate(val) ? val : ""
      }

    return {
      headerTooltip: abbrevationMap[fieldName],
      headerName: fieldName,
      children: [
        {
          headerName: "Baseline",
          colId: `${dateKey}__baseline`,
          valueGetter: safeValueGetter("baseline"),
          ...commonDateSubColDef,
        },
        {
          headerName: "Forecast",
          colId: `${dateKey}__forecast`,
          valueGetter: safeValueGetter("forecast"),
          ...commonDateSubColDef,
        },
        {
          headerName: "Actual",
          colId: `${dateKey}__actual`,
          valueGetter: safeValueGetter("actual"),
          ...commonDateSubColDef,
        },
      ],
    }
  })
}

const formsColDef = () => {
  return {
    headerName: "Form(s)",
    field: "forms",
    filter: "agTextColumnFilter",
    width: 250,
    cellRenderer: ActionItemForms,
  }
}

const docsColDef = () => {
  return {
    headerName: "File(s)",
    field: "docs",
    filter: "agTextColumnFilter",
    cellRenderer: ActionItemDocs,
  }
}

const getCountryColDef = ({ field, headerName }) => {
  const CountryCellRenderer = (params) => <CountryFlags countries={params.value || []} />

  return {
    headerName,
    field,
    cellRenderer: CountryCellRenderer,
    filter: false,
  }
}

const getEsignColDef = () => {
  return {
    headerName: "eSign",
    field: "esign_failed",
    cellRenderer: "SuccessFailedCellRenderer",
    width: 120,
    filterParams: {
      valueFormatter: (params) => {
        if (params.value === true) return "Failed"
        if (params.value === false) return "Successful"
        return params.value
      },
    },
  }
}

const getOutcomeColDef = () => {
  return {
    headerName: "Outcome",
    field: "outcome",
    valueGetter: ({ data }) => {
      if (data.is_approved) return "Approved"
      else if (data.is_rejected) return "Rejected"
      else if (data.is_onhold) return "On Hold"
      else return null
    },
  }
}

export {
  dateColDef,
  getDateColDef,
  getDateColDefMonthYear,
  getDateColDefSeconds,
  dateFormatter,
  getRolesColDef,
  largeColDef,
  favoritesColDef,
  getRecordStatusColDef,
  phaseColDef,
  getEditColDef,
  getDeleteColDef,
  actionPriorityColDef,
  openFileColDef,
  uploadStatusColDef,
  formsColDefCsrm,
  getMilestoneDateFieldCols,
  formsColDef,
  docsColDef,
  getCountryColDef,
  getEsignColDef,
  getOutcomeColDef,
}
